import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, MapPin, Plus, Phone, Mail, ChevronRight, CheckCircle2, Clock, XCircle, Search, Ticket } from "lucide-react";
import Header from "@/components/Header";
import { useLang } from "@/i18n";
import api, { apiError } from "@/lib/api";

const today = () => new Date().toISOString().slice(0, 10);
const devLifecycle = (d) => {
  const c = d.closes_on;
  if (c && c <= today()) return "closed";
  if (c && c > today()) return "soon";
  return "active";
};

const TABS = [
  { id: "all", de: "Alle", en: "All", icon: MapPin },
  { id: "active", de: "Aktiv", en: "Active", icon: CheckCircle2 },
  { id: "soon", de: "Schließt bald", en: "Closing soon", icon: Clock },
  { id: "closed", de: "Geschlossen", en: "Closed", icon: XCircle },
];

const telHref = (v) => `tel:${String(v).replace(/[^+\d]/g, "")}`;

export default function Locations() {
  const { lang } = useLang();
  const de = lang === "de";
  const navigate = useNavigate();
  const [devices, setDevices] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [tab, setTab] = useState("all");
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [dr, tr] = await Promise.all([api.get("/devices"), api.get("/tickets", { params: { status: "open" } })]);
      setDevices(dr.data || []);
      setTickets(tr.data || []);
    } catch (err) { toast.error(apiError(err)); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const sx = (d, k) => String((d.extra || {})[k] || "").trim();

  // Offene Tickets je Standort
  const openByLoc = useMemo(() => {
    const m = {};
    tickets.forEach((t) => { if (t.status === "open") m[t.location_id] = (m[t.location_id] || 0) + 1; });
    return m;
  }, [tickets]);

  // Geräte nach Standortcode (location_id) gruppieren
  const locations = useMemo(() => {
    const groups = {};
    devices.forEach((d) => {
      const lid = d.location_id || d.device_code || "—";
      (groups[lid] = groups[lid] || []).push(d);
    });
    const pick = (devs, key) => { for (const d of devs) { const v = sx(d, key); if (v) return v; } return ""; };
    return Object.entries(groups).map(([lid, devs]) => {
      const ds = [...devs].sort((a, b) => (a.device_code || "").localeCompare(b.device_code || ""));
      const name = ds.map((d) => d.location_name).find(Boolean) || pick(ds, "Stationsname") || pick(ds, "Ort");
      const straße = pick(ds, "Straße"), plz = pick(ds, "PLZ"), ort = pick(ds, "Ort"), adr = pick(ds, "Adresse");
      const address = [straße, [plz, ort].filter(Boolean).join(" ")].filter(Boolean).join(", ") || adr;
      const lcs = ds.map(devLifecycle);
      const lc = lcs.every((x) => x === "closed") ? "closed" : lcs.some((x) => x === "soon") ? "soon" : "active";
      return {
        lid,
        name,
        address,
        ort,
        phone: pick(ds, "Telefon"),
        email: pick(ds, "E-Mail Team"),
        manager: pick(ds, "LocManager"),
        agentur: pick(ds, "Agentur"),
        devices: ds,
        lc,
        openTickets: openByLoc[lid] || 0,
      };
    }).sort((a, b) => a.lid.localeCompare(b.lid));
  }, [devices, openByLoc]);

  const counts = { all: locations.length, active: 0, soon: 0, closed: 0 };
  locations.forEach((l) => { counts[l.lc]++; });

  const q = query.trim().toLowerCase();
  const matchQ = (l) => {
    if (!q) return true;
    const hay = [l.lid, l.name, l.ort, l.address, l.manager, l.phone, l.email, ...l.devices.map((d) => d.device_code)].join(" ").toLowerCase();
    return hay.includes(q);
  };
  const shown = (tab === "all" ? locations : locations.filter((l) => l.lc === tab)).filter(matchQ);

  const newStation = async () => {
    const code = window.prompt(de ? "Geräte-Code der neuen Station (z. B. FRAT03-01):" : "Device code:", "");
    if (!code) return;
    const name = window.prompt(de ? "Standort-Name (optional):" : "Location name (optional):", "") || "";
    try { await api.post("/devices/create", { device_code: code.trim(), location_name: name }); toast.success(code.trim()); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  const badge = (lc) => lc === "closed"
    ? "text-red-700 bg-red-500/15 border-red-500/30"
    : lc === "soon" ? "text-amber-700 bg-amber-500/15 border-amber-500/30" : "text-[#c00000] bg-[#c00000]/10 border-[#c00000]/30";
  const badgeLabel = (lc) => lc === "closed" ? "closed" : lc === "soon" ? (de ? "schließt bald" : "closing soon") : (de ? "aktiv" : "active");

  const stop = (e) => e.stopPropagation();

  return (
    <div className="min-h-screen bg-white text-gray-800">
      <Header />
      <div className="w-full px-4 sm:px-6 py-6">
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-3">
            <MapPin className="w-6 h-6 text-[#c00000]" />
            <h1 className="text-2xl font-bold text-gray-900">{de ? "Standorte" : "Locations"}</h1>
          </div>
          <button data-testid="loc-new" onClick={newStation} className="flex items-center gap-2 bg-gray-50 border border-gray-200 text-gray-800 rounded-lg px-3 py-2 text-sm hover:bg-gray-100 transition-colors">
            <Plus className="w-4 h-4" /> {de ? "Neue Station" : "New station"}
          </button>
        </div>
        <p className="text-sm text-gray-600 mb-6">{de ? "Gruppiert nach Standortcode. Klick auf eine Kachel öffnet die Standort-Detailseite." : "Grouped by location code. Click a tile to open the location detail page."}</p>

        <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-6">
          <div className="relative flex-1 max-w-md">
            <Search className="w-4 h-4 text-gray-600 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              data-testid="loc-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={de ? "Standort suchen (Code, Ort, Adresse, Leiter…)" : "Search location (code, city, address, manager…)"}
              className="w-full bg-gray-50 border border-gray-200 rounded-lg pl-9 pr-3 py-2 text-sm text-gray-800 placeholder-gray-500 focus:outline-none focus:border-[#c00000]/50"
            />
          </div>
          <div className="flex flex-wrap gap-2">
          {TABS.map((tt) => {
            const Icon = tt.icon;
            return (
              <button key={tt.id} data-testid={`loc-tab-${tt.id}`} onClick={() => setTab(tt.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm border transition-colors ${tab === tt.id ? "bg-gray-100 border-gray-300 text-gray-900" : "bg-gray-50 border-gray-200 text-gray-600 hover:text-gray-900"}`}>
                <Icon className="w-4 h-4" /> {de ? tt.de : tt.en} <span className="text-gray-600">({counts[tt.id]})</span>
              </button>
            );
          })}
          </div>
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-gray-600"><Loader2 className="w-4 h-4 animate-spin" /> …</div>
        ) : shown.length === 0 ? (
          <div className="text-gray-600 text-sm py-10 text-center">{de ? "Keine Standorte in dieser Kategorie." : "No locations."}</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="locations-grid">
            {shown.map((l) => (
              <div
                key={l.lid}
                data-testid={`loc-card-${l.lid}`}
                onClick={() => navigate(`/locations/${encodeURIComponent(l.lid)}`)}
                className="group cursor-pointer bg-gray-50 border border-gray-200 rounded-xl p-4 hover:border-[#c00000]/40 hover:bg-gray-50 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-[#c00000]/5 transition-all duration-200"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-base font-bold text-gray-900">{l.lid}</span>
                      <span className="text-[11px] text-gray-600">· {l.devices.length} {de ? "Geräte" : "devices"}</span>
                    </div>
                    <div className="text-sm text-gray-600 truncate">{l.name || "—"}</div>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    {l.openTickets > 0 && (
                      <span data-testid={`loc-ticket-badge-${l.lid}`} className="flex items-center gap-1 text-[10px] font-mono px-1.5 py-0.5 rounded border text-amber-700 bg-amber-500/15 border-amber-500/30">
                        <Ticket className="w-3 h-3" /> {l.openTickets}
                      </span>
                    )}
                    <span className={`text-[10px] font-mono uppercase px-1.5 py-0.5 rounded border ${badge(l.lc)}`}>{badgeLabel(l.lc)}</span>
                  </div>
                </div>

                <div className="mt-3 space-y-1.5 text-xs">
                  {l.address && <div className="text-gray-700">{l.address}</div>}
                  {l.manager && <div className="text-gray-600">{de ? "Leiter" : "Manager"}: {l.manager}</div>}
                  <div className="flex flex-wrap gap-x-4 gap-y-1 pt-0.5">
                    {l.phone && (
                      <a data-testid={`loc-phone-${l.lid}`} href={telHref(l.phone)} onClick={stop} className="flex items-center gap-1.5 text-[#c00000] hover:text-[#c00000] hover:underline">
                        <Phone className="w-3.5 h-3.5" /> {l.phone}
                      </a>
                    )}
                    {l.email && (
                      <a data-testid={`loc-email-${l.lid}`} href={`mailto:${l.email}`} onClick={stop} className="flex items-center gap-1.5 text-[#c00000] hover:text-[#c00000] hover:underline truncate max-w-full">
                        <Mail className="w-3.5 h-3.5 shrink-0" /> <span className="truncate">{l.email}</span>
                      </a>
                    )}
                  </div>
                </div>

                <div className="mt-3 flex flex-wrap gap-1.5">
                  {l.devices.map((d) => (
                    <button
                      key={d.device_code}
                      data-testid={`loc-dev-chip-${d.device_code}`}
                      onClick={(e) => { stop(e); navigate(`/d/${encodeURIComponent(d.device_code)}`); }}
                      title={d.location_name || d.device_code}
                      className="font-mono text-[11px] px-2 py-0.5 rounded border border-gray-200 bg-gray-50 text-gray-700 hover:border-[#c00000]/50 hover:text-[#c00000] transition-colors"
                    >
                      {d.device_code}
                    </button>
                  ))}
                </div>

                <div className="mt-3 flex items-center justify-end text-[11px] text-gray-600 group-hover:text-[#c00000] transition-colors">
                  {de ? "Details" : "Details"} <ChevronRight className="w-4 h-4" />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
